/**
 * Voxelizer (Voxel-Skinner port) — triangle mesh → classified {@link VoxelGrid}.
 * ONE responsibility: the accepted two-pass solid voxelization —
 *   1. shell detection: mark voxels a triangle passes through as BOUNDARY
 *      (Separating Axis Theorem triangle–AABB test, Akenine-Möller 2001);
 *   2. flood-fill: flood EMPTY inward from the padded shell; every voxel the
 *      flood never reaches is INTERIOR.
 * This is a faithful TypeScript port of the accepted zero-dependency algorithm —
 * unchanged math, added types. It knows nothing of E1's grid contract; the
 * bridge to E1 lives one level up in hands/voxelize.ts.
 *
 * VoxelState stays INTERNAL to this voxel/ subdir (ADR-010 §4). The adapter
 * consumes occupancy through {@link occupiedVoxelIndices}, never the raw states.
 *
 * Depends on: hands/voxel/voxel-grid-vs (VoxelGrid, VoxelState). Zero external
 * deps — no three.js, no DOM, no timers (verified against the upstream lib).
 * Depended on by: hands/voxelize.
 *
 * Credit: vendored port of `@p0qp0q/voxel-skinner` `src/Voxelizer.js`
 * (Dionne & de Lasa geodesic voxel-binding lineage per its README; SAT method
 * from Akenine-Möller, "Fast 3D Triangle-Box Overlap Testing", 2001).
 */
import { VoxelGrid, VoxelState } from './voxel-grid-vs.js';

/** A 3-component vector as a fixed-length tuple (literal-indexed, so type-clean). */
type Vec3 = [number, number, number];

/** Flat vertex positions `[x,y,z, x,y,z, ...]`. */
export type Positions = Float32Array | Float64Array | readonly number[];
/** Triangle indices (3 per face); `null`/`undefined` ⇒ non-indexed positions. */
export type Indices = Uint32Array | Uint16Array | readonly number[] | null | undefined;

/**
 * Voxelize a triangle mesh into a classified grid.
 *
 * @param positions  Flat vertex positions `[x,y,z, ...]`.
 * @param indices    Triangle indices (3 per face), or null for non-indexed.
 * @param resolution Voxels along the longest axis (default 64).
 */
export function voxelize(positions: Positions, indices: Indices, resolution = 64): VoxelGrid {
  const bounds = computeBounds(positions);

  // Expand bounds slightly so surfaces flush with the AABB don't fall on edges.
  const pad = 0.001;
  bounds.min[0] -= pad;
  bounds.min[1] -= pad;
  bounds.min[2] -= pad;
  bounds.max[0] += pad;
  bounds.max[1] += pad;
  bounds.max[2] += pad;

  const grid = new VoxelGrid(bounds, resolution);

  markShell(grid, positions, indices);
  floodFillExterior(grid);

  return grid;
}

/**
 * VS-native linear indices of every OCCUPIED voxel (INTERIOR ∪ BOUNDARY),
 * ascending. This is the subdir's occupancy contract — the adapter bridges these
 * to E1's index convention without ever seeing a {@link VoxelState}.
 */
export function occupiedVoxelIndices(grid: VoxelGrid): number[] {
  const out: number[] = [];
  for (let i = 0; i < grid.totalVoxels; i += 1) {
    const s = grid.state[i];
    if (s === VoxelState.BOUNDARY || s === VoxelState.INTERIOR) out.push(i);
  }
  return out;
}

/** Axis-aligned bounding box of a flat position array (mutable for padding). */
function computeBounds(positions: Positions): { min: Vec3; max: Vec3 } {
  const min: Vec3 = [Infinity, Infinity, Infinity];
  const max: Vec3 = [-Infinity, -Infinity, -Infinity];

  for (let i = 0; i < positions.length; i += 3) {
    const x = positions[i]!;
    const y = positions[i + 1]!;
    const z = positions[i + 2]!;
    if (x < min[0]) min[0] = x;
    if (x > max[0]) max[0] = x;
    if (y < min[1]) min[1] = y;
    if (y > max[1]) max[1] = y;
    if (z < min[2]) min[2] = z;
    if (z > max[2]) max[2] = z;
  }

  return { min, max };
}

/** Mark every voxel a mesh triangle intersects as BOUNDARY. */
function markShell(grid: VoxelGrid, positions: Positions, indices: Indices): void {
  const triCount = indices
    ? Math.floor(indices.length / 3)
    : Math.floor(positions.length / 9);

  for (let t = 0; t < triCount; t += 1) {
    let i0: number;
    let i1: number;
    let i2: number;
    if (indices) {
      i0 = indices[t * 3]! * 3;
      i1 = indices[t * 3 + 1]! * 3;
      i2 = indices[t * 3 + 2]! * 3;
    } else {
      i0 = t * 9;
      i1 = t * 9 + 3;
      i2 = t * 9 + 6;
    }

    const v0: Vec3 = [positions[i0]!, positions[i0 + 1]!, positions[i0 + 2]!];
    const v1: Vec3 = [positions[i1]!, positions[i1 + 1]!, positions[i1 + 2]!];
    const v2: Vec3 = [positions[i2]!, positions[i2 + 1]!, positions[i2 + 2]!];

    // Triangle bounding box in world space.
    const tMin: Vec3 = [
      Math.min(v0[0], v1[0], v2[0]),
      Math.min(v0[1], v1[1], v2[1]),
      Math.min(v0[2], v1[2], v2[2]),
    ];
    const tMax: Vec3 = [
      Math.max(v0[0], v1[0], v2[0]),
      Math.max(v0[1], v1[1], v2[1]),
      Math.max(v0[2], v1[2], v2[2]),
    ];

    // Candidate voxel range (conservative, clamped by voxelCoords).
    const vMin = grid.voxelCoords(tMin[0], tMin[1], tMin[2]);
    const vMax = grid.voxelCoords(tMax[0], tMax[1], tMax[2]);

    for (let vz = vMin[2]; vz <= vMax[2]; vz += 1) {
      for (let vy = vMin[1]; vy <= vMax[1]; vy += 1) {
        for (let vx = vMin[0]; vx <= vMax[0]; vx += 1) {
          if (grid.getState(vx, vy, vz) === VoxelState.BOUNDARY) continue;

          const center = grid.worldPos(vx, vy, vz);
          const halfSize = grid.voxelSize * 0.5;

          if (triangleAABBIntersect(v0, v1, v2, center, halfSize)) {
            grid.setState(vx, vy, vz, VoxelState.BOUNDARY);
          }
        }
      }
    }
  }
}

/**
 * Flood EMPTY inward from the grid's padded shell; every voxel the flood never
 * reaches (and that isn't BOUNDARY) is reclassified INTERIOR.
 */
function floodFillExterior(grid: VoxelGrid): void {
  const { resX, resY, resZ } = grid;
  const VISITED = 255; // temporary marker

  const queue: number[] = [];

  // Seed every face voxel of the grid (guaranteed exterior thanks to padding).
  for (let z = 0; z < resZ; z += 1) {
    for (let y = 0; y < resY; y += 1) {
      for (let x = 0; x < resX; x += 1) {
        if (x === 0 || x === resX - 1 || y === 0 || y === resY - 1 || z === 0 || z === resZ - 1) {
          const idx = grid.index(x, y, z);
          if (grid.state[idx] === VoxelState.EMPTY) {
            grid.state[idx] = VISITED;
            queue.push(idx);
          }
        }
      }
    }
  }

  // 6-connected flood fill.
  const offsets: readonly number[] = [-1, 0, 0, 1, 0, 0, 0, -1, 0, 0, 1, 0, 0, 0, -1, 0, 0, 1];

  let head = 0;
  while (head < queue.length) {
    const idx = queue[head]!;
    head += 1;
    const [cx, cy, cz] = grid.coords(idx);

    for (let n = 0; n < 18; n += 3) {
      const nx = cx + offsets[n]!;
      const ny = cy + offsets[n + 1]!;
      const nz = cz + offsets[n + 2]!;

      if (!grid.inBounds(nx, ny, nz)) continue;

      const nIdx = grid.index(nx, ny, nz);
      if (grid.state[nIdx] === VoxelState.EMPTY) {
        grid.state[nIdx] = VISITED;
        queue.push(nIdx);
      }
    }
  }

  // Final classification: VISITED → EMPTY, any still-EMPTY → INTERIOR.
  for (let i = 0; i < grid.totalVoxels; i += 1) {
    if (grid.state[i] === VISITED) {
      grid.state[i] = VoxelState.EMPTY;
    } else if (grid.state[i] === VoxelState.EMPTY) {
      grid.state[i] = VoxelState.INTERIOR;
    }
    // BOUNDARY stays as-is.
  }
}

// ──────────────────────────────────────────────────────────────
// Triangle–AABB intersection (Akenine-Möller / SAT method).
// ──────────────────────────────────────────────────────────────

/**
 * Whether a triangle intersects an axis-aligned cube of half-extent `halfSize`
 * centred at `center` (SAT, Akenine-Möller 2001).
 */
function triangleAABBIntersect(v0: Vec3, v1: Vec3, v2: Vec3, center: Vec3, halfSize: number): boolean {
  // Translate the triangle so the AABB sits at the origin.
  const a0: Vec3 = [v0[0] - center[0], v0[1] - center[1], v0[2] - center[2]];
  const a1: Vec3 = [v1[0] - center[0], v1[1] - center[1], v1[2] - center[2]];
  const a2: Vec3 = [v2[0] - center[0], v2[1] - center[1], v2[2] - center[2]];

  const h = halfSize;

  // Edge vectors.
  const e0: Vec3 = [a1[0] - a0[0], a1[1] - a0[1], a1[2] - a0[2]];
  const e1: Vec3 = [a2[0] - a1[0], a2[1] - a1[1], a2[2] - a1[2]];
  const e2: Vec3 = [a0[0] - a2[0], a0[1] - a2[1], a0[2] - a2[2]];

  // 9 edge × box-axis cross-product separating axes.
  if (!testCrossAxes(a0, a1, a2, e0, e1, e2, h)) return false;

  // 3 AABB face normals.
  if (Math.min(a0[0], a1[0], a2[0]) > h || Math.max(a0[0], a1[0], a2[0]) < -h) return false;
  if (Math.min(a0[1], a1[1], a2[1]) > h || Math.max(a0[1], a1[1], a2[1]) < -h) return false;
  if (Math.min(a0[2], a1[2], a2[2]) > h || Math.max(a0[2], a1[2], a2[2]) < -h) return false;

  // Triangle face normal.
  const normal = cross(e0, e1);
  const d = dot(normal, a0);
  const r = h * (Math.abs(normal[0]) + Math.abs(normal[1]) + Math.abs(normal[2]));
  if (d > r || d < -r) return false;

  return true;
}

function testCrossAxes(a0: Vec3, a1: Vec3, a2: Vec3, e0: Vec3, e1: Vec3, e2: Vec3, h: number): boolean {
  const edges: readonly Vec3[] = [e0, e1, e2];
  const verts: readonly Vec3[] = [a0, a1, a2];

  for (let i = 0; i < 3; i += 1) {
    const e = edges[i]!;
    for (let axis = 0; axis < 3; axis += 1) {
      const ax1 = (axis + 1) % 3;
      const ax2 = (axis + 2) % 3;

      // Project the three vertices onto the candidate separating axis.
      const p0 = -e[ax2]! * verts[0]![ax1]! + e[ax1]! * verts[0]![ax2]!;
      const p1 = -e[ax2]! * verts[1]![ax1]! + e[ax1]! * verts[1]![ax2]!;
      const p2 = -e[ax2]! * verts[2]![ax1]! + e[ax1]! * verts[2]![ax2]!;

      const pMin = Math.min(p0, p1, p2);
      const pMax = Math.max(p0, p1, p2);

      // Project the box onto the same axis.
      const r = h * (Math.abs(e[ax2]!) + Math.abs(e[ax1]!));

      if (pMin > r || pMax < -r) return false;
    }
  }
  return true;
}

function cross(a: Vec3, b: Vec3): Vec3 {
  return [a[1] * b[2] - a[2] * b[1], a[2] * b[0] - a[0] * b[2], a[0] * b[1] - a[1] * b[0]];
}

function dot(a: Vec3, b: Vec3): number {
  return a[0] * b[0] + a[1] * b[1] + a[2] * b[2];
}
